import { useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert, Box, Breadcrumbs, Button, Chip, Grid, IconButton, Link, MenuItem, Paper, Select,
  Snackbar, Stack, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import type { ActionItem, Stage } from '../api/types';
import { STAGE_ORDER } from '../api/types';
import {
  useChangeOpportunityOwner, useCustomerContacts, useDeleteOpportunity, useMoveStage,
  useOpportunity, useOpportunityActions, useOpportunityHistory,
} from '../api/hooks';
import {
  ConfirmDialog, ErrorState, LoadingState, StageChip, UserText, useErrorMessage,
} from '../components/common/Common';
import { OpportunityFormDialog } from '../components/forms/OpportunityFormDialog';
import { ActionFormDialog } from '../components/forms/ActionFormDialog';
import { OwnerPickerDialog } from '../components/forms/OwnerPickerDialog';
import { ActionTimeline } from '../components/timeline/ActionTimeline';
import { CloseOpportunityDialog, CloseResult } from '../components/board/CloseOpportunityDialog';
import { formatCurrency, formatDate, formatDateTime, formatPercent } from '../components/common/format';

export function OpportunityDetailPage() {
  const { id } = useParams();
  const opportunityId = Number(id);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const describeError = useErrorMessage();

  const { data: opportunity, isLoading, error, refetch } = useOpportunity(opportunityId);
  const { data: history } = useOpportunityHistory(opportunityId);
  const { data: contacts } = useCustomerContacts(opportunity?.customerId ?? 0);
  const { data: opportunityActions } = useOpportunityActions(opportunityId);
  const moveStage = useMoveStage();
  const changeOwner = useChangeOpportunityOwner();
  const remove = useDeleteOpportunity();

  const [editOpen, setEditOpen] = useState(false);
  const [actionDialog, setActionDialog] = useState<{ open: boolean; action: ActionItem | null }>({ open: false, action: null });
  const [ownerOpen, setOwnerOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  if (isLoading) return <LoadingState label={t('opportunity.loading')} />;
  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;
  if (!opportunity) return null;

  const changeStage = (to: Stage) => {
    if (to === opportunity.stage) return;
    if (to === 'closed') {
      setClosing(true);
      return;
    }
    moveStage.mutate(
      { id: opportunityId, move: { toStage: to, closedOutcome: null, lostReason: null } },
      { onError: (e) => setToast(describeError(e)) },
    );
  };

  const confirmClose = (result: CloseResult) => {
    setClosing(false);
    moveStage.mutate(
      { id: opportunityId, move: { toStage: 'closed', closedOutcome: result.outcome, lostReason: result.lostReason } },
      { onError: (e) => setToast(describeError(e)) },
    );
  };

  return (
    <Box>
      <Breadcrumbs sx={{ mb: 1 }}>
        <Link component={RouterLink} to="/" underline="hover" color="text.secondary">{t('nav.pipeline')}</Link>
        <Link component={RouterLink} to={`/customers/${opportunity.customerId}`} underline="hover" color="text.secondary">
          <UserText>{opportunity.customerName}</UserText>
        </Link>
        <Typography color="text.primary"><UserText>{opportunity.title}</UserText></Typography>
      </Breadcrumbs>

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        spacing={2}
        sx={{ mb: 2.5 }}
      >
        <Box>
          <Typography variant="h1"><UserText>{opportunity.title}</UserText></Typography>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
            <StageChip stage={opportunity.stage} outcome={opportunity.closedOutcome} />
            <Typography variant="body2" color="text.secondary">
              {t('opportunity.probabilityAndSource', {
                probability: formatPercent(opportunity.probability),
                source: opportunity.source,
              })}
            </Typography>
          </Stack>
        </Box>

        <Stack direction="row" spacing={1} alignItems="center">
          <Select
            size="small"
            value={opportunity.stage}
            onChange={(e) => changeStage(e.target.value as Stage)}
            data-testid="stage-select"
            sx={{ minWidth: 150 }}
          >
            {STAGE_ORDER.map((s) => <MenuItem key={s} value={s}>{t(`stage.${s}`)}</MenuItem>)}
          </Select>
          <Button startIcon={<EditIcon />} onClick={() => setEditOpen(true)} data-testid="edit-opportunity">
            {t('common.edit')}
          </Button>
          <Button startIcon={<DeleteOutlineIcon />} color="error" onClick={() => setDeleting(true)}>
            {t('common.delete')}
          </Button>
        </Stack>
      </Stack>

      {opportunity.closedOutcome === 'lost' && opportunity.lostReason && (
        <Alert severity="warning" sx={{ mb: 2.5 }} data-testid="lost-reason-banner">
          <Typography variant="overline">{t('opportunity.whyLost')}</Typography>
          <Typography variant="body2"><UserText multiline>{opportunity.lostReason}</UserText></Typography>
        </Alert>
      )}

      <Grid container spacing={2.5}>
        <Grid size={{ xs: 12, md: 7 }}>
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: '10px', mb: 2.5 }}>
            <Typography variant="overline" color="text.secondary">{t('opportunity.solutionSelling')}</Typography>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Field label={t('opportunity.pain')} value={opportunity.pain} testId="detail-pain" />
              <Field label={t('opportunity.vision')} value={opportunity.vision} testId="detail-vision" />
              <Field label={t('opportunity.value')} value={opportunity.value} testId="detail-value" />
            </Stack>
          </Paper>

          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
            <Typography variant="h2">{t('opportunity.activity')}</Typography>
            <Button
              startIcon={<AddIcon />}
              variant="contained"
              onClick={() => setActionDialog({ open: true, action: null })}
              data-testid="new-action"
            >
              {t('opportunity.logAction')}
            </Button>
          </Stack>
          <ActionTimeline
            actions={opportunityActions?.items ?? []}
            onEdit={(a) => setActionDialog({ open: true, action: a })}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 5 }}>
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: '10px', mb: 2.5 }}>
            <Typography variant="overline" color="text.secondary">{t('opportunity.money')}</Typography>
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              <Row label={t('opportunity.estimatedValue')} value={formatCurrency(opportunity.estimatedValue, opportunity.currency)} />
              <Row label={t('opportunity.expectedClose')} value={formatDate(opportunity.expectedCloseDate)} />
              <Row label={t('opportunity.probability')} value={`${formatPercent(opportunity.probability)} %`} />
              {opportunity.closedAtUtc && (
                <Row label={t('opportunity.closedOn')} value={formatDateTime(opportunity.closedAtUtc)} />
              )}
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: '10px', mb: 2.5 }}>
            <Typography variant="overline" color="text.secondary">{t('opportunity.people')}</Typography>
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              <Row label={t('opportunity.champion')} value={opportunity.championName ?? '—'} />
              <Row label={t('opportunity.economicBuyer')} value={opportunity.economicBuyerName ?? '—'} />
              <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                <Typography variant="body2" color="text.secondary">{t('owner.owner')}</Typography>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right' }} data-testid="opportunity-owner">
                    {opportunity.ownerName
                      ? <UserText>{opportunity.ownerName}</UserText>
                      : <Box component="span" sx={{ color: 'text.disabled' }}>{t('owner.unassigned')}</Box>}
                  </Typography>
                  <Tooltip title={t('owner.changeOwner')}>
                    <IconButton size="small" onClick={() => setOwnerOpen(true)} data-testid="change-owner">
                      <EditIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Stack>
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: '10px', mb: 2.5 }} data-testid="partner-block">
            <Typography variant="overline" color="text.secondary">{t('partner.partner')}</Typography>
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              <Row label={t('partner.partner')} value={opportunity.partnerName ?? '—'} />
              {opportunity.partnerRelationshipType && (
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                  <Typography variant="body2" color="text.secondary">{t('partner.relationshipType')}</Typography>
                  <Chip size="small" variant="outlined" label={t(`partner.relationship.${opportunity.partnerRelationshipType}`)} />
                </Stack>
              )}
              {opportunity.partnerMarginPercent !== null && (
                <Row label={t('partner.margin')} value={`${formatPercent(opportunity.partnerMarginPercent)} %`} />
              )}
              {opportunity.partnerIsInherited && (
                <Typography variant="caption" color="text.secondary" data-testid="partner-inherited-caption">
                  {t('partner.inheritedFromCustomer')}
                </Typography>
              )}
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: '10px', mb: 2.5 }}>
            <Typography variant="overline" color="text.secondary">{t('opportunity.nextStep')}</Typography>
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              <Row label={t('opportunity.nextStepWhat')} value={opportunity.nextStep ?? '—'} />
              <Row label={t('opportunity.due')} value={formatDate(opportunity.nextStepDueDate)} />
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: '10px' }}>
            <Typography variant="overline" color="text.secondary">{t('opportunity.stageHistory')}</Typography>
            <Stack spacing={1} sx={{ mt: 1 }} data-testid="stage-history">
              {(history ?? []).map((h, i) => (
                <Box key={i} data-testid={`history-row-${i}`}>
                  <Typography variant="body2">
                    {h.fromStage
                      ? t('opportunity.stageTransition', { from: t(`stage.${h.fromStage}`), to: t(`stage.${h.toStage}`) })
                      : t('opportunity.createdAtStage', { stage: t(`stage.${h.toStage}`) })}
                    {h.closedOutcome && ` (${t(`outcome.${h.closedOutcome}`)})`}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {h.changedByName} · {formatDateTime(h.changedAtUtc)}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      <OpportunityFormDialog
        open={editOpen}
        customerId={opportunity.customerId}
        contacts={contacts ?? []}
        opportunity={opportunity}
        onClose={() => setEditOpen(false)}
      />
      <ActionFormDialog
        open={actionDialog.open}
        customerId={opportunity.customerId}
        opportunityId={opportunityId}
        action={actionDialog.action}
        onClose={() => setActionDialog({ open: false, action: null })}
      />
      <OwnerPickerDialog
        open={ownerOpen}
        currentOwnerUserId={opportunity.ownerUserId}
        busy={changeOwner.isPending}
        error={changeOwner.error}
        onClose={() => setOwnerOpen(false)}
        onSave={async (ownerUserId) => {
          await changeOwner.mutateAsync({ id: opportunityId, ownerUserId });
          setOwnerOpen(false);
        }}
      />
      <CloseOpportunityDialog
        open={closing}
        title={opportunity.title}
        onConfirm={confirmClose}
        onCancel={() => setClosing(false)}
      />
      <ConfirmDialog
        open={deleting}
        title={t('opportunity.deleteTitle')}
        message={t('opportunity.deleteMessage')}
        busy={remove.isPending}
        onClose={() => setDeleting(false)}
        onConfirm={async () => {
          await remove.mutateAsync(opportunityId);
          setDeleting(false);
          navigate('/');
        }}
      />

      <Snackbar open={Boolean(toast)} autoHideDuration={6000} onClose={() => setToast(null)}>
        <Alert severity="error" onClose={() => setToast(null)}>{toast}</Alert>
      </Snackbar>
    </Box>
  );
}

function Field({ label, value, testId }: { label: string; value: string | null; testId: string }) {
  const { t } = useTranslation();
  return (
    <Box>
      <Typography variant="overline" color="text.secondary" sx={{ display: 'block' }}>{label}</Typography>
      <Typography variant="body1" color={value ? 'text.primary' : 'text.disabled'} data-testid={testId}>
        {value ? <UserText multiline>{value}</UserText> : t('opportunity.notCaptured')}
      </Typography>
    </Box>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <Stack direction="row" justifyContent="space-between" spacing={2}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right' }}>
        <UserText>{value}</UserText>
      </Typography>
    </Stack>
  );
}
